import { initializeApp, getApps } from 'firebase/app';
import {
  getAnalytics,
  isSupported,
  logEvent as firebaseLogEvent,
  setConsent,
} from 'firebase/analytics';
import { firebaseConfig } from './firebase.config';

const COOKIE_CONSENT_KEY = 'logiword_cookie_consent';

let appInstance = null;
let analyticsInstance = null;

export const GameEvents = {
  GAME_STARTED: 'game_started',
  GAME_COMPLETED: 'game_completed',
  WORD_GUESSED: 'word_guessed',
  LANGUAGE_CHANGED: 'language_changed',
  PREVIOUS_DAY_PLAYED: 'previous_day_played',
  SETTINGS_OPENED: 'settings_opened',
};

function hasAnalyticsConsent() {
  try {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(COOKIE_CONSENT_KEY) === 'functional';
  } catch {
    return false;
  }
}

async function ensureAnalytics() {
  if (!hasAnalyticsConsent()) return null;
  if (typeof window === 'undefined') return null;
  const supported = await isSupported().catch(() => false);
  if (!supported) return null;
  if (!appInstance) {
    const apps = getApps();
    appInstance = apps.length ? apps[0] : initializeApp(firebaseConfig);
  }
  if (!analyticsInstance) {
    analyticsInstance = getAnalytics(appInstance);
  }
  return analyticsInstance;
}

/**
 * When the Firebase Analytics JS SDK is unsupported (isSupported() === false) or fails,
 * send the same events via gtag → GA4. Matches mobile/analytics.js web behavior.
 */
async function sendViaGtag(eventName, params = {}) {
  const measurementId = firebaseConfig.measurementId;
  if (!measurementId || typeof window === 'undefined') return false;

  try {
    if (!window.gtag) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      document.head.appendChild(script);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('config', measurementId);
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
    if (window.gtag) {
      window.gtag('event', eventName, params);
      return true;
    }
  } catch {
    // non-fatal
  }
  return false;
}

/** Align Firebase consent with cookie choice (call when consent is known). */
export async function syncWebAnalyticsConsent(cookieConsent) {
  try {
    if (cookieConsent === 'functional') {
      const a = await ensureAnalytics();
      if (a) {
        setConsent({
          ad_personalization: 'denied',
          ad_user_data: 'denied',
          analytics_storage: 'granted',
        });
      }
    } else if (cookieConsent === 'none' && analyticsInstance) {
      setConsent({
        analytics_storage: 'denied',
      });
    }
  } catch {
    // non-fatal
  }
}

export async function logEvent(eventName, params = {}) {
  if (!hasAnalyticsConsent()) return;
  try {
    const a = await ensureAnalytics();
    if (a) {
      firebaseLogEvent(a, eventName, params);
      return;
    }
  } catch {
    // fall through to gtag
  }
  await sendViaGtag(eventName, params);
}

export async function logScreenView(screenName, screenClass = null) {
  const screenParams = {
    screen_name: screenName,
    screen_class: screenClass || screenName,
  };
  await logEvent('screen_view', screenParams);
}
